import React, {
    createContext, useContext, useState, useEffect,
    useCallback, useRef, ReactNode,
} from 'react';
import { supabase, DBUser } from '@/lib/supabase';
import { lovable } from '@/integrations/lovable';
import type { Session } from '@supabase/supabase-js';

// ── Types ─────────────────────────────────────────────────────

/** Kept for backwards compat with existing components. */
export interface GoogleProfile {
    sub: string;
    email: string;
    name: string;
    picture: string;
    given_name?: string;
}

export interface UserProfile extends DBUser {
    displayName: string;
    photoURL: string;
}

interface AuthContextType {
    googleProfile: GoogleProfile | null;
    userProfile: UserProfile | null;
    isGuest: boolean;
    isLoading: boolean;
    isAuthenticated: boolean;

    loginWithGoogle: () => Promise<void>;
    loginWithEmail: (email: string, password: string) => Promise<{ error?: string }>;
    signUpWithEmail: (email: string, password: string, displayName?: string) => Promise<{ error?: string }>;
    loginAsGuest: () => void;
    logout: () => Promise<void>;
    updateProfile: (patch: Partial<UserProfile>) => void;

    permanentPeerId: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const GUEST_KEY = 'tg_guest';

function safeSessionGet(key: string): string | null {
    try { return window.sessionStorage.getItem(key); } catch { return null; }
}

// ── Provider ──────────────────────────────────────────────────

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [session, setSession] = useState<Session | null>(null);
    const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
    const [isGuest, setIsGuest] = useState(() => safeSessionGet(GUEST_KEY) === '1');
    const [isLoading, setIsLoading] = useState(true);
    const onlineInterval = useRef<ReturnType<typeof setInterval> | null>(null);

    const permanentPeerId = userProfile?.peer_id ?? null;

    // Build a "google-style" profile from the supabase session for backwards compat
    const googleProfile: GoogleProfile | null = session?.user
        ? {
            sub: (session.user.user_metadata?.sub as string) || session.user.id,
            email: session.user.email ?? '',
            name: (session.user.user_metadata?.full_name as string)
                ?? (session.user.user_metadata?.name as string)
                ?? session.user.email
                ?? '',
            picture: (session.user.user_metadata?.avatar_url as string)
                ?? (session.user.user_metadata?.picture as string)
                ?? '',
        }
        : null;

    // ── Load the public.users profile row ─────────────────────
    const loadProfile = useCallback(async (userId: string): Promise<UserProfile | null> => {
        // Brief retry — the on-signup trigger may take a moment
        for (let i = 0; i < 5; i++) {
            const { data, error } = await supabase
                .from('users')
                .select('*')
                .eq('id', userId)
                .maybeSingle();
            if (data) {
                return {
                    ...(data as any),
                    displayName: (data as any).display_name,
                    photoURL: (data as any).photo_url ?? '',
                } as UserProfile;
            }
            if (error) console.warn('[Auth] loadProfile error:', error.message);
            await new Promise(r => setTimeout(r, 400));
        }
        return null;
    }, []);

    // ── Online presence heartbeat ─────────────────────────────
    const startPresence = useCallback((userId: string, peerId: string) => {
        const ping = () => {
            supabase
                .from('users')
                .update({ is_online: true, last_seen: new Date().toISOString(), peer_id: peerId })
                .eq('id', userId)
                .then(() => {});
        };
        ping();
        onlineInterval.current = setInterval(ping, 30_000);

        const markOffline = () => {
            supabase.from('users')
                .update({ is_online: false, last_seen: new Date().toISOString() })
                .eq('id', userId);
        };
        window.addEventListener('beforeunload', markOffline);
        return () => {
            if (onlineInterval.current) clearInterval(onlineInterval.current);
            window.removeEventListener('beforeunload', markOffline);
            markOffline();
        };
    }, []);

    // ── Auth actions ──────────────────────────────────────────
    const loginWithGoogle = useCallback(async () => {
        const result = await lovable.auth.signInWithOAuth('google', {
            redirect_uri: window.location.origin,
        });
        if (result.error) {
            console.error('[Auth] Google sign-in failed:', result.error);
        }
    }, []);

    const loginWithEmail = useCallback(async (email: string, password: string) => {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        return { error: error?.message };
    }, []);

    const signUpWithEmail = useCallback(async (email: string, password: string, displayName?: string) => {
        const { error } = await supabase.auth.signUp({
            email,
            password,
            options: {
                emailRedirectTo: `${window.location.origin}/`,
                data: { display_name: displayName || email.split('@')[0] },
            },
        });
        return { error: error?.message };
    }, []);

    const loginAsGuest = useCallback(() => {
        sessionStorage.setItem(GUEST_KEY, '1');
        setIsGuest(true);
    }, []);

    const logout = useCallback(async () => {
        if (onlineInterval.current) clearInterval(onlineInterval.current);
        if (userProfile) {
            await supabase.from('users')
                .update({ is_online: false })
                .eq('id', userProfile.id);
        }
        await supabase.auth.signOut();
        sessionStorage.removeItem(GUEST_KEY);
        setIsGuest(false);
        setUserProfile(null);
    }, [userProfile]);

    const updateProfile = useCallback((patch: Partial<UserProfile>) => {
        setUserProfile(prev => {
            if (!prev) return prev;
            const next = { ...prev, ...patch };
            if (patch.display_name) next.displayName = patch.display_name;
            if (patch.photo_url) next.photoURL = patch.photo_url;
            return next;
        });
    }, []);

    // ── Session bootstrap + listener ──────────────────────────
    useEffect(() => {
        let cleanupPresence: (() => void) | null = null;

        // Listener FIRST, then getSession
        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, newSession) => {
            setSession(newSession);
            if (newSession?.user) {
                // Defer Supabase calls to avoid deadlock inside the callback
                setTimeout(async () => {
                    const profile = await loadProfile(newSession.user.id);
                    setUserProfile(profile);
                    if (profile) {
                        if (cleanupPresence) cleanupPresence();
                        cleanupPresence = startPresence(profile.id, profile.peer_id);
                    }
                }, 0);
            } else {
                if (cleanupPresence) { cleanupPresence(); cleanupPresence = null; }
                setUserProfile(null);
            }
        });

        supabase.auth.getSession().then(async ({ data: { session: existing } }) => {
            setSession(existing);
            if (existing?.user) {
                const profile = await loadProfile(existing.user.id);
                setUserProfile(profile);
                if (profile) cleanupPresence = startPresence(profile.id, profile.peer_id);
            }
            setIsLoading(false);
        });

        return () => {
            subscription.unsubscribe();
            if (cleanupPresence) cleanupPresence();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isAuthenticated = !!session || isGuest;

    return (
        <AuthContext.Provider value={{
            googleProfile, userProfile, isGuest, isLoading, isAuthenticated,
            loginWithGoogle, loginWithEmail, signUpWithEmail,
            loginAsGuest, logout, updateProfile, permanentPeerId,
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
    return ctx;
};
